import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Warning, Lock, ShieldCheck } from "@phosphor-icons/react";

/**
 * Return-to-Release + PER-PART Maximum Quota status banner.
 *
 * Fetches an engineer's pending returns and per-part quotas from
 * /engineers/{id}/pending-parts, then flags any parts in the current form
 * that would either (a) collide with an outstanding return or (b) push the
 * engineer past a configured per-part cap.
 *
 * Props:
 *   engineerId       : string
 *   parts            : [{name, stock_code, quantity}]
 *   isManager        : boolean
 *   override         : boolean
 *   onOverrideChange : (bool) => void
 *   onStatusChange   : ({blocked, hasLock, hasQuota, info}) => void
 *   quotaBlocks      : boolean — when false, quota breaches show as warning
 *                      but do NOT contribute to `blocked`. Use this on the
 *                      Raise Request dialog since the backend defers quota
 *                      enforcement to release time.
 *   testidPrefix     : string
 */
export default function LockStatusBanner({
  engineerId, parts,
  isManager = false, override = false, onOverrideChange = () => {},
  onStatusChange = () => {},
  quotaBlocks = true,
  testidPrefix = "lock",
}) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!engineerId) { setInfo(null); return; }
    setLoading(true);
    api.get(`/engineers/${engineerId}/pending-parts`)
      .then((res) => { if (alive) setInfo(res.data); })
      .catch(() => { if (alive) setInfo(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [engineerId]);

  const partKey = (p) => {
    const sc = (p?.stock_code || "").trim().toLowerCase();
    if (sc) return `sc:${sc}`;
    return `nm:${(p?.name || "").trim().toLowerCase()}`;
  };

  const { lockedParts, quotaBreaches } = useMemo(() => {
    if (!info) return { lockedParts: [], quotaBreaches: [] };

    // Return-to-Release lookups
    const pendingByKey = new Map();
    (info.pending_parts || []).forEach((p) => {
      const list = pendingByKey.get(p.key) || [];
      list.push(p);
      pendingByKey.set(p.key, list);
    });

    // Per-part quotas + current held
    const quotaByKey = new Map();
    (info.part_quotas || []).forEach((q) => {
      quotaByKey.set(q.key, q);
    });
    const heldByKey = info.held_by_part || {};

    // Aggregate the incoming form's qty per part
    const addingByKey = new Map();
    const labelByKey = new Map();
    (parts || []).forEach((p) => {
      const nm = (p?.name || "").trim();
      const qty = Math.max(0, Number(p?.quantity) || 0);
      if (!nm || qty <= 0) return;
      const k = partKey(p);
      addingByKey.set(k, (addingByKey.get(k) || 0) + qty);
      if (!labelByKey.has(k)) labelByKey.set(k, nm);
    });

    // Locked = the return-lock hit list
    const locked = [];
    addingByKey.forEach((_qty, k) => {
      const hits = pendingByKey.get(k);
      if (hits && hits.length) {
        locked.push({
          name: labelByKey.get(k),
          receipts: [...new Set(hits.map((h) => h.receipt_no || (h.record_id || "").slice(0, 8)))],
        });
      }
    });

    // Quota breaches = per-part caps exceeded by projected total
    const breaches = [];
    addingByKey.forEach((add, k) => {
      const q = quotaByKey.get(k);
      if (!q) return;
      const cap = Number(q.max_qty || 0);
      if (cap <= 0) return;
      const held = Number(heldByKey[k] || 0);
      const projected = held + add;
      if (projected > cap) {
        breaches.push({
          name: labelByKey.get(k) || q.name,
          cap, held, adding: add, projected,
        });
      }
    });

    return { lockedParts: locked, quotaBreaches: breaches };
  }, [info, parts]);

  const blocked = (lockedParts.length > 0 || (quotaBlocks && quotaBreaches.length > 0)) && !(override && isManager);

  useEffect(() => {
    onStatusChange({
      blocked,
      hasLock: lockedParts.length > 0,
      hasQuota: quotaBreaches.length > 0,
      info,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked, lockedParts.length, quotaBreaches.length, info]);

  if (!engineerId) return null;
  if (loading && !info) {
    return (
      <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-500 font-mono" data-testid={`${testidPrefix}-loading`}>
        Checking pending returns & per-part quotas…
      </div>
    );
  }
  if (!info) return null;

  const nQuotas = (info.part_quotas || []).length;
  const tone = blocked
    ? "border-red-500 bg-red-50 text-red-900"
    : (lockedParts.length || quotaBreaches.length)
      ? "border-amber-500 bg-amber-50 text-amber-900"
      : "border-neutral-200 bg-neutral-50 text-neutral-700";

  return (
    <div className={`border px-3 py-2 text-xs space-y-1.5 ${tone}`} data-testid={`${testidPrefix}-banner`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 font-mono uppercase tracking-wider text-[10px]">
          <Lock size={12} weight={blocked ? "fill" : "bold"} />
          Return-to-Release · Per-part Quota
        </div>
        <div className="font-mono text-[10px]">
          {nQuotas > 0
            ? <>{nQuotas} quota{nQuotas === 1 ? "" : "s"} configured</>
            : <span className="text-neutral-500">no per-part caps set</span>}
        </div>
      </div>

      {lockedParts.length > 0 && (
        <div data-testid={`${testidPrefix}-return-lock`}>
          <div className="flex items-center gap-1 font-semibold">
            <Warning size={12} weight="fill" /> Pending return{lockedParts.length > 1 ? "s" : ""}
          </div>
          <ul className="mt-0.5 ml-4 list-disc marker:text-current">
            {lockedParts.map((l) => (
              <li key={`lock:${l.name}`}>
                <span className="font-medium">{l.name}</span>{" "}
                <span className="font-mono text-[10px] opacity-80">on {l.receipts.join(", ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {quotaBreaches.length > 0 && (
        <div data-testid={`${testidPrefix}-quota-exceeded`}>
          <div className="flex items-center gap-1 font-semibold">
            <Warning size={12} weight="fill" /> Quota exceeded{quotaBlocks ? "" : " (advisory — will be re-checked at release)"}
          </div>
          <ul className="mt-0.5 ml-4 list-disc marker:text-current">
            {quotaBreaches.map((b) => (
              <li key={`quota:${b.name}`}>
                <span className="font-medium">{b.name}</span>{" "}
                <span className="font-mono text-[10px] opacity-80">
                  holding {b.held} + adding {b.adding} = {b.projected} · cap {b.cap}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(lockedParts.length > 0 || (quotaBlocks && quotaBreaches.length > 0)) && (
        isManager ? (
          <label className="flex items-center gap-2 pt-1 cursor-pointer select-none" data-testid={`${testidPrefix}-override-wrap`}>
            <input
              type="checkbox"
              checked={override}
              onChange={(e) => onOverrideChange(e.target.checked)}
              className="accent-neutral-900"
              data-testid={`${testidPrefix}-override`}
            />
            <span className="flex items-center gap-1 font-medium">
              <ShieldCheck size={12} weight="bold" /> Manager override — proceed anyway
            </span>
          </label>
        ) : (
          <div className="pt-1 text-[11px] italic opacity-90" data-testid={`${testidPrefix}-manager-only`}>
            Only a Manager can override this — ask a manager to review.
          </div>
        )
      )}
    </div>
  );
}
